import React, { FC } from 'react';
import Box from '@mui/material/Box';
import { orange, red } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import MUITooltip, { TooltipProps } from '@mui/material/Tooltip';
import { SystemCssProperties } from '@mui/system/styleFunctionSx';

import { InfoFilledIcon } from './icons';

interface Props extends TooltipProps {
  variant?: 'default' | 'info' | 'error' | 'warning';
  showIcon?: boolean;
}

export const Tooltip: FC<Props> = ({
  variant = 'default',
  showIcon = false,
  title,
  children,
  placement = 'top',
  sx = {},
  ...props
}) => {
  const content = title ? (
    <Stack direction="row">
      {showIcon && (
        <Box sx={{ mr: 1 }}>
          <InfoFilledIcon size={20} />
        </Box>
      )}
      <div>{title}</div>
    </Stack>
  ) : (
    ''
  );

  const variantStyle = getVariant(variant);

  const defaultComponentProps = !variantStyle
    ? { tooltip: { sx: { ...(sx as SystemCssProperties) } } }
    : {
        tooltip: {
          sx: {
            padding: '8px 12px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: variantStyle.color,
            backgroundColor: variantStyle.backgroundColor,
            zIndex: 1300,
            ...(sx as SystemCssProperties),
          },
        },
        arrow: {
          sx: {
            color: variantStyle.backgroundColor,
          },
        },
      };

  return (
    <MUITooltip
      {...props}
      componentsProps={{ ...defaultComponentProps, ...props?.componentsProps }}
      placement={placement}
      title={content}
      arrow
    >
      {children}
    </MUITooltip>
  );
};

const getVariant = (variant: Props['variant']): { color: string; backgroundColor: string } | undefined => {
  switch (variant) {
    case 'info':
      return { color: '#363c4e', backgroundColor: '#edeff2' };
    case 'warning':
      return { color: orange[800], backgroundColor: orange[50] };
    case 'error':
      return { color: red[700], backgroundColor: red[50] };
    case 'default':
    default:
      return;
  }
};
